import { Box, Button, Flex, Icon, Link, TableIconButton, Td, Text, Tr, useToast, useTranslation } from "@familyzone/component-library";
import { forEach } from "lodash";
import React, { useEffect, useState } from "react";
import { displayTypesArray, NamedEntry, ObjectPool, YoutubeDisplayTypeIndex } from "../../../types/Objects";
import { TableColumn } from "../../../types/table";
import { downcaseDomain, stripSchema } from "../../../utils/WebsiteUtil";
import TableBasedPage from "../../templates/TableBasedPage";
import AddNewObjectEntry from "./AddNewObjectEntry";
import DeleteObjectPoolsModal from "./DeleteObjectPoolsModal";
import { validateObject } from "../../../utils/Validation";
import ImportCSVModal from "./ImportCSVModal";
import EditObjectPoolsYoutube from "./EditObjectPoolsYoutube";
import { useObjectStore } from "../../../storez/ObjectStore";

export interface NamedEntryProps extends NamedEntry {
  startName?: string;
  startValue?: string;
}

export interface CSVErrorProps {
  line: number;
  error: string;
  entry: string;
}

const EditObjectPools: React.FC = () => {
  const { errorToast } = useToast();
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [object, setObject] = useState<ObjectPool>();
  const [openNewEntry, setOpenNewEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState<NamedEntryProps | null>(null);
  const [openDeleteEntry, setOpenDeleteEntry] = useState(false);
  const [listEntries, setListEntries] = useState<NamedEntryProps[]>([]);
  const [examples, setExamples] = useState<string[]>([]);
  const [openCSVImportModal, setOpenCSVImportModal] = useState(false);
  const WEBSITE_TYPE = 2;

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config/device/dashboard", isActive: false },
    { title: t("Objects"), url: "/config/device/objects/pools", isActive: false },
    { title: t("Pools"), url: "/config/device/objects/pools", isActive: false },
  ];

  const { fetch, getLoaded, getObject, saveObject } = useObjectStore();

  const onChange = () => {
    setLoaded(false);
    if (getLoaded()) {
      const params = window.location.href.split("/config/device/objects/pools/")?.[1]?.split("/");
      const result = getObject(params?.[0]);
      if (result) {
        result.displayType = displayTypesArray.find((displayType) => displayType[0] === result?.type)?.[1].toString();
      }
      setObject(result);

      const new_list_entries: NamedEntryProps[] = [];
      if (result !== undefined) {
        if (result.named_entries === undefined) {
          forEach(object?.named_entries, (value: NamedEntry, index: number) => {
            new_list_entries.push({
              id: index,
              name: value?.name ? value.name : "",
              desc: value?.desc ? value.desc : "",
              value: value?.value ? value.value : "",
              click_action: "edit",
              invalidValueMsg: null,
            });
          });
        } else {
          forEach(result.named_entries, (value: NamedEntry, index: number) => {
            new_list_entries.push({
              id: index,
              name: value?.name ? value.name : "",
              desc: value?.desc ? value.desc : "",
              value: value?.value ? value.value : "",
              click_action: "edit",
              invalidValueMsg: null,
            });
          });
        }
        setListEntries(new_list_entries);
      }
    }
    setLoaded(true);
  };

  useEffect(() => {
    const unsubscribe = useObjectStore.subscribe(onChange);

    void (async () => {
      setLoaded(false);
      await fetch();
      onChange();
      setLoaded(true);
    })();

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenEditEntry = (entry: NamedEntryProps) => {
    setOpenNewEntry(true);
    if (!entry) {
      return;
    }
    setEditingEntry({ ...entry, click_action: "edit", startName: entry.name, startValue: entry.value });
  };

  const handleCloseEditEntry = () => {
    setOpenNewEntry(false);
    setEditingEntry(null);
  };

  const handleOpenAddEntry = () => {
    setEditingEntry(null);
    setOpenNewEntry(true);
  };

  const handleOpenDeleteEntry = (entry: NamedEntryProps) => {
    setEditingEntry(entry);
    setOpenDeleteEntry(true);
  };

  const handleCloseDeleteEntry = () => {
    setOpenDeleteEntry(false);
    setEditingEntry(null);
  };

  const handleSubmitEntry = (entry: NamedEntry) => {
    if (!object) {
      return;
    }
    const strippedValue = stripSchema(entry.value);
    const error = validateObject(strippedValue, object);
    if (error) {
      errorToast({ title: error, position: "top", duration: 6000 });
      return;
    }
    // ignore and throw error if entry already exists
    if (listEntries.find((value) => value.value === strippedValue && entry.id !== value.id)) {
      errorToast({ title: "Entry already exists", position: "top", duration: 6000 });
      return;
    }
    if (!entry.name || shouldAutofillName(entry)) {
      autoFillName(entry);
    }
    if (!object?.named_entries) {
      object.named_entries = [];
    }
    if (entry.click_action === "edit") {
      listEntries.forEach((named_entry) => {
        if (named_entry.id === entry.id) {
          named_entry.name = entry.name;
          named_entry.desc = entry.desc;
          named_entry.value = strippedValue;
        }
      });
    } else {
      const newEntry = { ...entry, value: strippedValue };
      listEntries.push(newEntry);
    }
    setEditingEntry(null);
    handleCloseEditEntry();
    updatePersistedObject(object);
    onChange();
  };

  const shouldAutofillName = (entry: NamedEntryProps) => {
    return entry.startName === entry.startValue && entry.name === entry.startName;
  };

  const autoFillName = (entry: NamedEntry) => {
    entry.name = entry.value;
  };

  const handleDelete = () => {
    if (!object) {
      return;
    }
    object.entries = [];
    object.named_entries = listEntries.filter((value: NamedEntry) => value.id !== editingEntry?.id);

    if (object.type === WEBSITE_TYPE) {
      object.named_entries = object.named_entries.map((value) => ({
        ...value,
        value: downcaseDomain(value.value),
      }));
    }
    setLoaded(false);
    object.named_entries.forEach((entry: NamedEntry) => {
      delete entry.id;
    });
    void saveObject(object);
    handleCloseDeleteEntry();
    onChange();
  };

  const updatePersistedObject = (object: ObjectPool) => {
    object.entries = []; // convert to new method
    object.named_entries = listEntries;
    if (object.type === WEBSITE_TYPE) {
      object.named_entries = object.named_entries.map((value) => ({
        ...value,
        value: downcaseDomain(value.value),
      }));
    }
    setLoaded(false);
    object.named_entries.forEach((entry: NamedEntry) => {
      delete entry.id;
    });
    void saveObject(object);
  };

  const columns: TableColumn[] = [
    {
      headerText: t("Name"),
      columnName: "name",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Description"),
      columnName: "desc",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Entry"),
      columnName: "value",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Operations"),
      columnName: "operations",
      alignment: "right",
    },
  ];

  const handleDownloadCSV = () => {
    if (!object || object.named_entries === undefined) {
      return;
    }

    setLoaded(false);
    const headers = ["Name", "Description", "Entry"];
    const csvData = object.named_entries.map((entry) => `${entry.name},${entry.desc},${entry.value}`).join("\n");
    const csvContent = [headers.join(","), csvData].join("\n");
    const element = document.createElement("a");
    const file = new Blob([csvContent], { type: "text/csv" });
    element.href = URL.createObjectURL(file);
    element.download = `${object.name}.csv`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
    document.body.removeChild(element);
    setLoaded(true);
  };

  const handleOnImport = (listEntries: NamedEntryProps[]) => {
    if (!object) {
      return;
    }
    setOpenCSVImportModal(false);
    setLoaded(false);
    setListEntries(listEntries);
    onChange();
  };

  const handleCloseCSVImportModal = () => {
    setOpenCSVImportModal(false);
  };

  useEffect(() => {
    let examples: string[] = [];
    switch (object?.type) {
      case displayTypesArray[0][0]:
        examples = ["192.168.0.1-192.168.0.255", "127.0.0.0-127.255.255.255", "172.16.0.0-172.31.255.255"];
        break;
      case displayTypesArray[1][0]:
        examples = ["192.168.1.0/255.255.255.0", "10.1.0.0/255.255.0.0", "93.184.216.34/32"];
        break;
      case WEBSITE_TYPE:
        examples = ["notpurple.com", "example.com"];
        break;
      case displayTypesArray[3][0]:
        examples = ["CE:B2:A4:C8:F0:EA", "6B:91:38:EB:8A:BF", "01:DB:91:49:09:C1"];
        break;
      case displayTypesArray[4][0]:
        examples = ["web games", "inappropriate content", "free movies online"];
        break;
      case displayTypesArray[5][0]:
        examples = ["notpurple.com", "example.com"];
        break;
      default:
        break;
    }
    setExamples(examples);
  }, [object?.type]);

  const exampleCSV = () => {
    const headers = ["Name", "Description", "Entry"];
    if (object?.type === undefined) {
      return;
    }
    // Convert examples into CSV outputs
    const notices = `optional column - leave blank if not required,optional column - leave blank if not required,required (delete this line before submitting)`;
    const exampleEntries = examples.map((example) => `,,${example}`).join("\n");
    const csvData = [headers, notices, exampleEntries].join("\n");
    const element = document.createElement("a");
    const file = new Blob([csvData], { type: "text/csv" });
    element.href = URL.createObjectURL(file);
    element.download = "example.csv";
    element.title = "Download example CSV file";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const tableDataMap = (row: NamedEntry, index: number): JSX.Element => (
    <Tr key={index}>
      <Td>
        <Text fontSize="md">{row.name}</Text>
      </Td>
      <Td>
        <Text fontSize="md">{row.desc}</Text>
      </Td>
      <Td>
        {object?.type === WEBSITE_TYPE || object?.displayType === displayTypesArray[5][1] ? (
          <Link
            href={`http://${row.value}`}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${row.value} in new tab`}
            data-testid="websiteLink"
          >
            <Text color="#094C99" fontSize="md" minWidth="200px">
              {row.value}
            </Text>
          </Link>
        ) : (
          <Text fontSize="md" minWidth="200px">
            {row.value}
          </Text>
        )}
      </Td>
      <Td>
        <Flex justifyContent="end">
          <Box mr="sp8">
            <TableIconButton
              onClick={() => handleOpenEditEntry(row)}
              icon={<Icon icon="fa-pencil" variant="solid" color="text.paragraph.light" />}
              aria-label={"Edit association"}
              data-testid={row.id ? `${row.id}-edit` : "edit"}
            />
          </Box>
          <TableIconButton
            icon={<Icon icon="fa-trash-can" variant="solid" color="text.paragraph.light" />}
            aria-label={"Delete association"}
            onClick={() => handleOpenDeleteEntry(row)}
            data-testid={row.id ? `${row.id}-delete` : "delete"}
          />
        </Flex>
      </Td>
    </Tr>
  );

  if (object && object.type === YoutubeDisplayTypeIndex) {
    return <EditObjectPoolsYoutube breadcrumbs={breadcrumbs} loaded={loaded} objectPool={object} />;
  }

  return (
    <>
      <TableBasedPage
        breadcrumbs={breadcrumbs}
        title={t(object?.name ? (object?.displayType ? `${object.name} - ${object.displayType}` : "Pools") : "Pools")}
        loaded={loaded}
        columns={columns}
        data={listEntries}
        tableDataMap={tableDataMap}
        childrenInTableHeader={
          <Flex justifyContent="end">
            {examples.length > 0 && (
              <Box mr="sp8">
                <Button variant="outline" onClick={exampleCSV}>
                  {t("Download CSV Template")}
                </Button>
              </Box>
            )}
            <Box mr="sp8">
              <Button variant="primary" onClick={() => setOpenCSVImportModal(true)} disabled={!loaded}>
                {t("Import CSV")}
              </Button>
            </Box>
            <Box mr="sp8">
              <Button
                variant="primary"
                disabled={!loaded || object?.named_entries?.length === 0}
                onClick={handleDownloadCSV}
                data-testid="Export CSV"
                aria-label="Export CSV"
              >
                {t("Export CSV")}
              </Button>
            </Box>

            <Box>
              <Button onClick={handleOpenAddEntry} variant="primary" data-testid="Add-new-entry">
                {t("Add New Entry")}
              </Button>
            </Box>
          </Flex>
        }
      />
      <AddNewObjectEntry
        open={openNewEntry}
        onClose={handleCloseEditEntry}
        loaded={loaded}
        existingEntry={editingEntry}
        onSave={handleSubmitEntry}
      />
      <DeleteObjectPoolsModal
        open={openDeleteEntry}
        onClose={handleCloseDeleteEntry}
        loaded={loaded}
        type="entry"
        onDelete={handleDelete}
        id={editingEntry?.id?.toString()}
      />
      <ImportCSVModal open={openCSVImportModal} onClose={handleCloseCSVImportModal} onImport={handleOnImport} object={object} />
    </>
  );
};

export default EditObjectPools;
